/**
 * Real-time list of the signed-in user's persistent memories.
 *
 * Subscribes to `users/{uid}/memories` ordered by `updatedAt` desc. Memories are
 * facts the agent learned from conversations (consolidated by the daily job) or
 * saved on request; they are curated here — approved ones feed the agent's
 * context. Creation is backend-only (Firestore rules deny client `create`).
 */
import type { MemoryDoc } from '@agente/shared'
import { onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { useAuth } from '@/lib/auth'
import { memoriesCol } from '@/lib/firestore'

export interface UseMemoriesResult {
  memories: MemoryDoc[]
  loading: boolean
  error: string | null
}

export function useMemories(): UseMemoriesResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [memories, setMemories] = useState<MemoryDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setMemories([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)

    const q = query(memoriesCol(uid), orderBy('updatedAt', 'desc'))
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setMemories(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
    return unsubscribe
  }, [uid])

  return { memories, loading, error }
}
